import React from 'react';
import { Link } from 'react-router-dom';
import { toDatetimeLocalVancouver, fromDatetimeLocalVancouver } from '../../lib/utils';
import { APP_TIMEZONE } from '../../config';
import axios from 'axios';
import { getApiBaseUrl } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import InvoiceChecks from './InvoiceChecks';

// Severity tokens are defined centrally in App.css / tailwind.config.js
// (--priority-*, --audience-owners) — see Theming.md.
const PRIORITY_BADGE = {
  urgent: 'bg-priorityUrgent/20 text-priorityUrgent',
  high: 'bg-priorityHigh/20 text-priorityHigh',
  medium: 'bg-priorityMedium/20 text-priorityMedium',
  low: 'bg-priorityLow/20 text-priorityLow'
};

function TicketApprovalView({ approval }) {
  return (
    <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{approval.payload.description}</p>
  );
}

function TicketApprovalForm({ value, onChange }) {
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Title</label>
        <input
          type="text"
          value={value.title}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
          className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Description</label>
        <textarea
          value={value.description}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
          rows={4}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Priority</label>
        <select
          value={value.priority}
          onChange={(e) => onChange({ ...value, priority: e.target.value })}
          className="px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
        >
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
      </div>
    </>
  );
}

function BulletinApprovalView({ approval }) {
  const event = approval.payload.calendarEvent;
  return (
    <>
      <p className="text-sm text-muted-foreground mt-1">{approval.payload.description}</p>
      {event && event.title && (
        <div className="mt-2 text-xs bg-primary/10 border border-primary/20 rounded p-2 space-y-0.5">
          <p className="font-medium text-foreground">📅 {event.title}</p>
          {event.startTime && (
            <p className="text-muted-foreground">
              {new Date(event.startTime).toLocaleString('en-US', { timeZone: APP_TIMEZONE })}
              {event.endTime ? ` – ${new Date(event.endTime).toLocaleString('en-US', { timeZone: APP_TIMEZONE })}` : ''}
            </p>
          )}
        </div>
      )}
    </>
  );
}

function BulletinApprovalForm({ value, onChange }) {
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Title</label>
        <input
          type="text"
          value={value.title}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
          className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Description</label>
        <textarea
          value={value.description}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
          rows={4}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Audience</label>
        <select
          value={value.audience}
          onChange={(e) => onChange({ ...value, audience: e.target.value })}
          className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
        >
          <option value="all">All Residents</option>
          <option value="owners">Owners Only</option>
        </select>
      </div>
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
          <input
            type="checkbox"
            checked={value.hasEvent}
            onChange={(e) => onChange({ ...value, hasEvent: e.target.checked })}
            className="rounded"
          />
          Linked Calendar Event
        </label>
        {value.hasEvent && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2 pl-5">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Event Title</label>
              <input
                type="text"
                value={value.eventTitle}
                onChange={(e) => onChange({ ...value, eventTitle: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                required={value.hasEvent}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Start</label>
              <input
                type="datetime-local"
                value={value.eventStart}
                onChange={(e) => onChange({ ...value, eventStart: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                required={value.hasEvent}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">End</label>
              <input
                type="datetime-local"
                value={value.eventEnd}
                onChange={(e) => onChange({ ...value, eventEnd: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                required={value.hasEvent}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function TicketUpdateApprovalView({ approval }) {
  const { content, status, priority } = approval.payload;
  return (
    <>
      {content && <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{content}</p>}
      {(status || priority) && (
        <p className="text-xs text-muted-foreground mt-1">
          {status && `Status → ${status}`}
          {status && priority && ' · '}
          {priority && `Priority → ${priority}`}
        </p>
      )}
    </>
  );
}

function TicketUpdateApprovalForm({ value, onChange }) {
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Update content</label>
        <textarea
          value={value.content}
          onChange={(e) => onChange({ ...value, content: e.target.value })}
          className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
          rows={3}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Status</label>
        <select
          value={value.status}
          onChange={(e) => onChange({ ...value, status: e.target.value })}
          className="px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
        >
          <option value="">No change</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Priority</label>
        <select
          value={value.priority}
          onChange={(e) => onChange({ ...value, priority: e.target.value })}
          className="px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
        >
          <option value="">No change</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
      </div>
    </>
  );
}

function CalendarEventApprovalView({ approval }) {
  const { eventTitle, title, description, startTime, endTime, recurrence } = approval.payload;
  const fmt = (t) => new Date(t).toLocaleString('en-US', { timeZone: APP_TIMEZONE });
  return (
    <>
      {eventTitle && !title && <p className="text-sm text-muted-foreground mt-1">Event: {eventTitle}</p>}
      {description && <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{description}</p>}
      {(startTime || endTime) && (
        <p className="text-xs text-muted-foreground mt-1">
          {startTime ? fmt(startTime) : '?'} – {endTime ? fmt(endTime) : '?'}
        </p>
      )}
      {recurrence && recurrence !== 'none' && (
        <p className="text-xs text-muted-foreground">Recurrence: {recurrence}</p>
      )}
    </>
  );
}

function CalendarEventApprovalForm({ value, onChange, showRecurrence }) {
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Title</label>
        <input
          type="text"
          value={value.title}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
          className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Description</label>
        <textarea
          value={value.description}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
          rows={3}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-foreground mb-1">Start</label>
          <input
            type="datetime-local"
            value={value.startTime}
            onChange={(e) => onChange({ ...value, startTime: e.target.value })}
            className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-foreground mb-1">End</label>
          <input
            type="datetime-local"
            value={value.endTime}
            onChange={(e) => onChange({ ...value, endTime: e.target.value })}
            className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
          />
        </div>
      </div>
      {showRecurrence && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Recurrence</label>
          <select
            value={value.recurrence}
            onChange={(e) => onChange({ ...value, recurrence: e.target.value })}
            className="px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
          >
            <option value="none">None</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </div>
      )}
    </>
  );
}

function CalendarCreateApprovalForm(props) {
  return <CalendarEventApprovalForm {...props} showRecurrence />;
}

function EmailApprovalView({ approval }) {
  const { to, cc, body } = approval.payload;
  return (
    <>
      <p className="text-xs text-muted-foreground mt-1">
        To: {to}
        {cc && cc.length > 0 && ` · Cc: ${cc.join(', ')}`}
      </p>
      <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{body}</p>
    </>
  );
}

function EmailApprovalForm({ value, onChange }) {
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">To</label>
        <input
          type="email"
          value={value.to}
          onChange={(e) => onChange({ ...value, to: e.target.value })}
          className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Cc (comma separated)</label>
        <input
          type="text"
          value={value.cc}
          onChange={(e) => onChange({ ...value, cc: e.target.value })}
          className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Subject</label>
        <input
          type="text"
          value={value.subject}
          onChange={(e) => onChange({ ...value, subject: e.target.value })}
          className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Body</label>
        <textarea
          value={value.body}
          onChange={(e) => onChange({ ...value, body: e.target.value })}
          className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
          rows={12}
          required
        />
      </div>
    </>
  );
}

function ProblemReportApprovalView({ approval }) {
  const condoSlug = approval.condo?.slug;
  return (
    <>
      <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{approval.payload.content}</p>
      <p className="text-xs text-muted-foreground mt-2">
        Council and admins were already emailed. Approving emails this post to all other residents.
        {condoSlug && (
          <>
            {' '}
            <Link to={`/${condoSlug}/forum/${approval.payload.forumPostId}`} className="text-primary hover:underline">View post</Link>
          </>
        )}
      </p>
    </>
  );
}

const shortDate = (value) => (value ? new Date(value).toLocaleDateString('en-CA', { timeZone: APP_TIMEZONE, month: 'short', day: 'numeric', year: 'numeric' }) : '—');

function AttachedFileButton({ approval }) {
  const { getToken } = useAuth();
  if (!approval.payload.file) return null;
  const download = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/api/admin/approvals/${approval._id}/file`, { headers: { Authorization: `Bearer ${getToken()}` }, responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = approval.payload.file.name;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err?.response?.status === 404 ? 'This file is not on this server.' : 'Failed to download the file');
    }
  };
  return <button type="button" onClick={download} className="text-primary hover:text-primary/80 text-sm">Download {approval.payload.file.name}</button>;
}

function InvoiceApprovalView({ approval }) {
  const p = approval.payload;
  return (
    <div className="mt-1 space-y-2">
      <p className="text-sm text-muted-foreground">
        {p.invoiceNumber ? `Invoice ${p.invoiceNumber} · ` : ''}{shortDate(p.invoiceDate)} · due {shortDate(p.dueDate)} · {formatAmount(p.subtotal)} + GST {formatAmount(p.gst)}
      </p>
      {p.description && <p className="text-sm text-muted-foreground">{p.description}</p>}
      <InvoiceChecks checks={p.checks} />
      <AttachedFileButton approval={approval} />
    </div>
  );
}

const invoiceField = (value, onChange, key, label, props = {}) => (
  <div>
    <label htmlFor={`invoice-${key}`} className="block text-sm font-medium text-foreground mb-1">{label}</label>
    <input id={`invoice-${key}`} value={value[key]} onChange={(e) => onChange({ ...value, [key]: e.target.value })} className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm" {...props} />
  </div>
);

function InvoiceApprovalForm({ value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {invoiceField(value, onChange, 'vendorName', 'Vendor')}
      {invoiceField(value, onChange, 'invoiceNumber', 'Invoice number')}
      {invoiceField(value, onChange, 'invoiceDate', 'Invoice date', { type: 'date' })}
      {invoiceField(value, onChange, 'dueDate', 'Due date', { type: 'date' })}
      {invoiceField(value, onChange, 'subtotal', 'Subtotal', { type: 'number', step: '0.01', min: '0' })}
      {invoiceField(value, onChange, 'gst', 'GST', { type: 'number', step: '0.01', min: '0' })}
      {invoiceField(value, onChange, 'total', 'Total', { type: 'number', step: '0.01', min: '0', required: true })}
      <div className="col-span-2">{invoiceField(value, onChange, 'description', 'What it is for')}</div>
    </div>
  );
}

function QuoteApprovalView({ approval }) {
  const p = approval.payload;
  return (
    <div className="mt-1 space-y-2">
      <p className="text-sm text-muted-foreground">{p.vendorId ? '' : `${p.vendorName} isn't in the vendor list yet · `}received {shortDate(p.receivedAt)}{p.validUntil ? ` · valid until ${shortDate(p.validUntil)}` : ''}</p>
      {p.notes && <p className="text-sm text-muted-foreground">{p.notes}</p>}
      <AttachedFileButton approval={approval} />
    </div>
  );
}

function JsonPayloadView({ approval }) {
  return (
    <pre className="text-xs text-muted-foreground bg-background border border-border rounded p-3 overflow-x-auto mt-1">
      {JSON.stringify(approval.payload, null, 2)}
    </pre>
  );
}

// Per-type renderer map — the frontend mirror of backend/services/approvalTypes.js.
// Adding a new approval type on the backend needs exactly one entry here.
export const APPROVAL_TYPES = {
  'ticket.create': {
    label: 'Ticket',
    icon: '🎫',
    title: (a) => a.payload.title,
    badges: (a) => [
      { text: a.payload.priority, className: PRIORITY_BADGE[a.payload.priority] || PRIORITY_BADGE.medium }
    ],
    View: TicketApprovalView,
    EditForm: TicketApprovalForm,
    toFormState: (payload) => ({
      title: payload.title || '',
      description: payload.description || '',
      priority: payload.priority || 'medium'
    }),
    toPayload: (state) => ({
      title: state.title,
      description: state.description,
      priority: state.priority
    })
  },
  'bulletin.create': {
    label: 'Bulletin',
    icon: '📋',
    title: (a) => a.payload.title,
    badges: (a) => {
      const badges = [];
      if (a.payload.audience === 'owners') {
        badges.push({ text: 'Owners Only', className: 'bg-audienceOwners/20 text-audienceOwners' });
      }
      if (a.payload.calendarEvent && a.payload.calendarEvent.title) {
        badges.push({ text: '📅 Has Event', className: 'bg-primary/20 text-primary' });
      }
      return badges;
    },
    View: BulletinApprovalView,
    EditForm: BulletinApprovalForm,
    toFormState: (payload) => {
      const hasEvent = !!(payload.calendarEvent && payload.calendarEvent.title);
      return {
        title: payload.title || '',
        description: payload.description || '',
        audience: payload.audience === 'owners' ? 'owners' : 'all',
        hasEvent,
        eventTitle: payload.calendarEvent?.title || '',
        eventStart: payload.calendarEvent?.startTime ? toDatetimeLocalVancouver(payload.calendarEvent.startTime) : '',
        eventEnd: payload.calendarEvent?.endTime ? toDatetimeLocalVancouver(payload.calendarEvent.endTime) : ''
      };
    },
    toPayload: (state) => ({
      title: state.title,
      description: state.description,
      audience: state.audience,
      calendarEvent: state.hasEvent && state.eventTitle
        ? {
            title: state.eventTitle,
            startTime: fromDatetimeLocalVancouver(state.eventStart).toISOString(),
            endTime: fromDatetimeLocalVancouver(state.eventEnd).toISOString()
          }
        : null
    })
  },
  'ticket.update': {
    label: 'Ticket Update',
    icon: '✏️',
    title: (a) => `Update: ${a.payload.ticketNumber || a.payload.ticketTitle || 'Ticket'}`,
    badges: (a) => {
      const badges = [];
      if (a.payload.status) badges.push({ text: `→ ${a.payload.status}`, className: 'bg-primary/20 text-primary' });
      if (a.payload.priority) badges.push({ text: a.payload.priority, className: PRIORITY_BADGE[a.payload.priority] || PRIORITY_BADGE.medium });
      return badges;
    },
    View: TicketUpdateApprovalView,
    EditForm: TicketUpdateApprovalForm,
    toFormState: (payload) => ({
      content: payload.content || '',
      status: payload.status || '',
      priority: payload.priority || ''
    }),
    toPayload: (state) => ({
      content: state.content || null,
      status: state.status || null,
      priority: state.priority || null
    })
  },
  'calendar.create': {
    label: 'Calendar Event',
    icon: '📅',
    title: (a) => a.payload.title,
    badges: (a) => (a.payload.recurrence && a.payload.recurrence !== 'none'
      ? [{ text: a.payload.recurrence, className: 'bg-primary/20 text-primary' }]
      : []),
    View: CalendarEventApprovalView,
    EditForm: CalendarCreateApprovalForm,
    toFormState: (payload) => ({
      title: payload.title || '',
      description: payload.description || '',
      startTime: payload.startTime ? toDatetimeLocalVancouver(payload.startTime) : '',
      endTime: payload.endTime ? toDatetimeLocalVancouver(payload.endTime) : '',
      recurrence: payload.recurrence || 'none'
    }),
    toPayload: (state) => ({
      title: state.title,
      description: state.description,
      startTime: fromDatetimeLocalVancouver(state.startTime).toISOString(),
      endTime: fromDatetimeLocalVancouver(state.endTime).toISOString(),
      recurrence: state.recurrence
    })
  },
  'calendar.update': {
    label: 'Calendar Event Update',
    icon: '📅',
    title: (a) => a.payload.title || a.payload.eventTitle || 'Calendar event update',
    badges: () => [],
    View: CalendarEventApprovalView,
    EditForm: CalendarEventApprovalForm,
    toFormState: (payload) => ({
      title: payload.title || '',
      description: payload.description || '',
      startTime: payload.startTime ? toDatetimeLocalVancouver(payload.startTime) : '',
      endTime: payload.endTime ? toDatetimeLocalVancouver(payload.endTime) : ''
    }),
    toPayload: (state) => ({
      title: state.title || null,
      // Preserve an intentional clear-to-empty: unlike title/dates, an empty
      // description is a valid value (see approvalTypes.js's calendar.update
      // validate — only `undefined`/`null` count as "not provided").
      description: state.description,
      startTime: state.startTime ? fromDatetimeLocalVancouver(state.startTime).toISOString() : null,
      endTime: state.endTime ? fromDatetimeLocalVancouver(state.endTime).toISOString() : null
    })
  },
  'action.send_email': {
    label: 'Email',
    icon: '✉️',
    title: (a) => a.payload.subject,
    badges: (a) => {
      const badges = [{ text: `To: ${a.payload.to}`, className: 'bg-primary/20 text-primary' }];
      const cc = a.payload.cc || [];
      if (cc.length > 0) {
        badges.push({ text: `+${cc.length} cc`, className: 'bg-muted text-muted-foreground' });
      }
      return badges;
    },
    View: EmailApprovalView,
    EditForm: EmailApprovalForm,
    toFormState: (payload) => ({
      to: payload.to || '',
      cc: (payload.cc || []).join(', '),
      subject: payload.subject || '',
      body: payload.body || ''
    }),
    toPayload: (state) => ({
      to: state.to,
      cc: state.cc.split(',').map(address => address.trim()).filter(Boolean),
      subject: state.subject,
      body: state.body
    })
  },
  'forum.broadcast': {
    label: 'Problem Report',
    icon: '⚠️',
    title: (a) => `Problem reported by ${a.payload.authorName || 'a resident'}`,
    badges: () => [{ text: 'Email all residents', className: 'bg-primary/20 text-primary' }],
    View: ProblemReportApprovalView,
    EditForm: null,
    toFormState: null,
    toPayload: null
  },
  'council.decision': {
    label: 'Council Decision',
    icon: '⚖️',
    title: (a) => a.payload.title,
    badges: (a) => [
      ...(a.payload.amount !== null && a.payload.amount !== undefined ? [{ text: formatAmount(a.payload.amount), className: 'bg-primary/20 text-primary' }] : []),
      ...(a.version > 1 ? [{ text: `Version ${a.version}`, className: 'bg-muted text-muted-foreground' }] : [])
    ],
    View: CouncilDecisionApprovalView,
    EditForm: CouncilDecisionApprovalForm,
    toFormState: (payload) => ({
      title: payload.title || '',
      description: payload.description || '',
      amount: payload.amount === null || payload.amount === undefined ? '' : String(payload.amount)
    }),
    toPayload: (state) => ({
      title: state.title,
      description: state.description,
      amount: state.amount === '' ? null : Number(state.amount)
    })
  },
  'invoice': {
    label: 'Invoice',
    icon: '🧾',
    title: (a) => `${a.payload.vendorName || 'Invoice'} · ${formatAmount(a.payload.total)}`,
    badges: (a) => {
      const bad = (a.payload.checks || []).filter((c) => c.level === 'bad').length;
      const warn = (a.payload.checks || []).filter((c) => c.level === 'warn').length;
      return [
        ...(bad ? [{ text: `${bad} problem${bad > 1 ? 's' : ''}`, className: 'bg-destructive/20 text-destructive' }] : []),
        ...(warn ? [{ text: `${warn} to check`, className: 'bg-warning/20 text-warning' }] : [])
      ];
    },
    View: InvoiceApprovalView,
    EditForm: InvoiceApprovalForm,
    toFormState: (p) => ({
      vendorName: p.vendorName || '', invoiceNumber: p.invoiceNumber || '',
      invoiceDate: p.invoiceDate ? p.invoiceDate.slice(0, 10) : '', dueDate: p.dueDate ? p.dueDate.slice(0, 10) : '',
      subtotal: String(p.subtotal ?? ''), gst: String(p.gst ?? ''), total: String(p.total ?? ''), description: p.description || ''
    }),
    toPayload: (s) => ({
      vendorName: s.vendorName, invoiceNumber: s.invoiceNumber, invoiceDate: s.invoiceDate || null, dueDate: s.dueDate || null,
      subtotal: s.subtotal === '' ? null : Number(s.subtotal), gst: s.gst === '' ? 0 : Number(s.gst), total: Number(s.total), description: s.description
    })
  },
  'quote.file': {
    label: 'Quote',
    icon: '📑',
    title: (a) => `${a.payload.vendorName}: ${a.payload.title} · ${formatAmount(a.payload.amount)}`,
    badges: () => [],
    View: QuoteApprovalView,
    EditForm: null,
    toFormState: null,
    toPayload: null
  }
};

export function formatAmount(amount) {
  return Number(amount).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
}

function CouncilDecisionApprovalView({ approval }) {
  return approval.payload.description
    ? <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-4">{approval.payload.description}</p>
    : null;
}

function CouncilDecisionApprovalForm({ value, onChange }) {
  return (
    <>
      <div>
        <label htmlFor="decision-title" className="block text-sm font-medium text-foreground mb-1">Title</label>
        <input id="decision-title" type="text" required value={value.title} onChange={(e) => onChange({ ...value, title: e.target.value })} className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm" />
      </div>
      <div>
        <label htmlFor="decision-description" className="block text-sm font-medium text-foreground mb-1">Description</label>
        <textarea id="decision-description" rows={4} value={value.description} onChange={(e) => onChange({ ...value, description: e.target.value })} className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm" />
      </div>
      <div>
        <label htmlFor="decision-amount" className="block text-sm font-medium text-foreground mb-1">Amount (optional)</label>
        <input id="decision-amount" type="number" min="0" step="0.01" value={value.amount} onChange={(e) => onChange({ ...value, amount: e.target.value })} className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm" />
      </div>
    </>
  );
}

// Fallback for any type the frontend doesn't know about yet — renders the raw
// payload with working Approve/Reject but no Edit, so the backend can ship a
// new approval type ahead of the frontend without this page crashing.
export const UNKNOWN_APPROVAL_TYPE = {
  label: 'Approval',
  icon: '📄',
  title: (a) => a.type,
  badges: (a) => [{ text: a.type, className: 'bg-muted text-muted-foreground' }],
  View: JsonPayloadView,
  EditForm: null,
  toFormState: null,
  toPayload: null
};

export function getApprovalType(type) {
  return APPROVAL_TYPES[type] || UNKNOWN_APPROVAL_TYPE;
}
